
import { logger } from '@/utils/logger';

/**
 * Checks for common API issues and provides diagnostic information
 */
export const checkApiIssues = (error: Error): string => {
  const errorMsg = error.message.toLowerCase();
  
  if (errorMsg.includes('api key') || errorMsg.includes('authentication') || errorMsg.includes('invalid key') || errorMsg.includes('401') || errorMsg.includes('unauthorized')) {
    logger.error('API key issue detected in FAQ generation', error, {
      component: 'faqSerp',
      tags: ['api-key-error'],
      context: {
        errorDetails: error.message,
        timestamp: new Date().toISOString()
      }
    });
    return 'API key issue detected. Check that your OpenAI API key is valid and has not expired.';
  }
  
  if (errorMsg.includes('rate limit') || errorMsg.includes('too many requests') || errorMsg.includes('429')) {
    logger.error('Rate limit exceeded in FAQ generation', error, {
      component: 'faqSerp',
      tags: ['rate-limit'],
      context: {
        errorDetails: error.message,
        timestamp: new Date().toISOString()
      }
    });
    return 'Rate limit exceeded. The API is receiving too many requests. Try again in a few minutes.';
  }
  
  if (errorMsg.includes('timeout') || errorMsg.includes('timed out')) {
    logger.error('Request timeout in FAQ generation', error, {
      component: 'faqSerp',
      tags: ['timeout'],
      context: {
        errorDetails: error.message,
        timestamp: new Date().toISOString()
      }
    });
    return 'Request timed out. The API took too long to respond. Check your network connection or try a shorter content sample.';
  }
  
  if (errorMsg.includes('content filter') || errorMsg.includes('content policy')) {
    logger.error('Content policy violation in FAQ generation', error, {
      component: 'faqSerp',
      tags: ['content-policy'],
      context: {
        errorDetails: error.message,
        timestamp: new Date().toISOString()
      }
    });
    return 'Content policy violation. The article content may contain material that violates OpenAI\'s content policy.';
  }
  
  if (errorMsg.includes('billing') || errorMsg.includes('payment') || errorMsg.includes('402')) {
    logger.error('Billing issue in FAQ generation', error, {
      component: 'faqSerp',
      tags: ['billing'],
      context: {
        errorDetails: error.message,
        timestamp: new Date().toISOString()
      }
    });
    return 'Billing issue detected. Your OpenAI account may have payment requirements or usage limits.';
  }
  
  if (errorMsg.includes('<!doctype') || errorMsg.includes('<html') || errorMsg.includes('unexpected token')) {
    logger.error('HTML response detected in FAQ generation', error, {
      component: 'faqSerp',
      tags: ['html-response'],
      context: {
        errorDetails: error.message.substring(0, 200), // First 200 chars to avoid huge logs
        timestamp: new Date().toISOString()
      }
    });
    return 'Received HTML instead of JSON. This typically indicates an API authentication or rate limiting issue.';
  }
  
  if (errorMsg.includes('syntax error') || errorMsg.includes('unexpected token') || errorMsg.includes('unexpected end of json input')) {
    logger.error('JSON parsing error in FAQ generation', error, {
      component: 'faqSerp',
      tags: ['json-error'],
      context: {
        errorDetails: error.message,
        timestamp: new Date().toISOString()
      }
    });
    return 'Error parsing API response. The API may have returned malformed JSON. Please try again.';
  }
  
  if (errorMsg.includes('network') || errorMsg.includes('connection') || errorMsg.includes('offline')) {
    logger.error('Network error in FAQ generation', error, {
      component: 'faqSerp',
      tags: ['network-error'],
      context: {
        errorDetails: error.message,
        timestamp: new Date().toISOString()
      }
    });
    return 'Network error. Check your internet connection and try again.';
  }
  
  if (errorMsg.includes('200') && errorMsg.includes('deepcrawl')) {
    logger.error('Incorrect response content in FAQ generation', error, {
      component: 'faqSerp',
      tags: ['incorrect-response'],
      context: {
        errorDetails: error.message,
        timestamp: new Date().toISOString()
      }
    });
    return 'Received incorrect response content. This may indicate a proxy or API gateway issue. Please try with a direct API key.';
  }
  
  // Log any unrecognized errors
  logger.error('Unrecognized error in FAQ generation', error, {
    component: 'faqSerp',
    tags: ['unknown-error'],
    context: {
      errorDetails: error.message,
      errorStack: error.stack,
      timestamp: new Date().toISOString()
    }
  });
  
  // Default detailed error message
  return `Error: ${error.message}. Check API status and try again.`;
};
